"use client"

import { WalletAddressLink } from "@/app/components/wallet-address"
import { ItemMedia } from "@/app/item/view/item-media"
import { PriceInUsd } from "@/app/price"
import { PriceInCrypto } from "@/app/price"
import type { NftMiniature } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans, useLingui } from "@lingui/react/macro"
import { Card, Skeleton } from "@rarible_int/anaconda_ui_components"
import { useHighestCollectionOffers } from "../../offers/hooks.reatom"
import { useCollectionFloorPrice } from "../../statistic/use-floor"
import { useCollectionNativeCurrency } from "../../store/use-collection"

function NftSingleSellSkeleton() {
  return (
    <>
      <Card className="flex gap-1 p-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-md" />
          <div className="flex flex-col gap-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </Card>
      <Card className="flex flex-col gap-1 p-4">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-24" />
      </Card>
    </>
  )
}

function NftPriceInfo({ nft }: { nft: NftMiniature }) {
  const { data: floorPrice } = useCollectionFloorPrice(nft.collection.id)

  if (!floorPrice && !nft.lastSellPrice) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-[10px] text-foreground/60 text-xs">
      {floorPrice && (
        <span>
          <Trans>Floor:</Trans> <PriceInCrypto {...floorPrice} />
        </span>
      )}
      {nft.lastSellPrice && (
        <span>
          <Trans>Last:</Trans> <PriceInCrypto {...nft.lastSellPrice} />
        </span>
      )}
    </div>
  )
}

interface Props {
  nft: NftMiniature | undefined
  sellPrice: PriceValue
}

export function NftSingleSell({ nft, sellPrice }: Props) {
  const { t } = useLingui()
  const nativeCurrency = useCollectionNativeCurrency()
  const offers = useHighestCollectionOffers()

  if (!nft) {
    return <NftSingleSellSkeleton />
  }

  return (
    <>
      <Card className="flex gap-1 p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10">
            <ItemMedia
              src={nft.image}
              alt={t`Image for ${nft.name} item`}
              className="rounded-md object-contain"
            />
          </div>
          <div className="flex flex-col gap-1">
            {nft.name}
            <NftPriceInfo nft={nft} />
          </div>
        </div>
      </Card>
      <Card className="flex flex-col gap-1 p-4">
        <span className="text-foreground/60">
          <Trans>Offer</Trans>
        </span>
        <div className="flex items-baseline gap-3">
          <PriceInCrypto
            className="font-headline text-2xl"
            amount={sellPrice?.amount ?? "0"}
            currency={nativeCurrency}
          />
          <PriceInUsd
            className="[&&]:text-base"
            amount={sellPrice?.amount ?? "0"}
            currency={nativeCurrency}
          />
        </div>
        {offers[0]?.order?.maker && (
          <div className="flex items-center gap-2 text-foreground/60">
            <Trans>From</Trans>{" "}
            <WalletAddressLink address={offers[0].order.maker} />
          </div>
        )}
      </Card>
    </>
  )
}
